import {
    api, C, sampleFields, sampleInventory, sampleTreatments,
    FRUITETI_CROPS, OPEN_FIELD_CROP_GROUPS, WMO_CODES,
    PERMANENT_CROP_KEYWORDS, OFFLINE_QUEUE_KEY
} from './context.js';
import {
    parseDateRobust, formatActivityTypeLabel, getFieldAreaFormatted,
    getUnitForProduct, getSafetyStatusBadge, formatDate, formatDateIT,
    activityMatchesField, getActivityCrop, isPermanentCrop
} from './utils.js';
import { showRegisterRequiredAlert } from './auth.js';
import { switchTab } from './nav.js';
import { loadApplicationData } from './data.js';
import { openFieldSummaryModal, focusMapOnField } from './map.js';
export function canTransmitSian() {
    if (!C.currentUser) return false;
    if (C.currentUser.isDemo) return true;
    const role = (C.currentUser.role || '').toLowerCase();
    return ['admin', 'agrotecnico', 'agronomo', 'perito_agrario'].includes(role);
}


export function countSianPendingActivities() {
    return (C.state.treatments || []).filter(t => !t.sianSyncId).length;
}

export function updateSianFilterButton() {
    const btn = document.getElementById('btn-filter-sian-pending');
    if (!btn) return;

    const show = canTransmitSian();
    btn.classList.toggle('hidden', !show);
    if (!show) {
        C.logSianPendingFilter = false;
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
        return;
    }

    const pending = countSianPendingActivities();
    btn.innerHTML = `<i class="fa-solid fa-filter"></i> Da trasmettere${pending > 0 ? `<span class="filter-count">${pending}</span>` : ''}`;
    btn.classList.toggle('active', C.logSianPendingFilter);
    btn.setAttribute('aria-pressed', C.logSianPendingFilter ? 'true' : 'false');
}

export function updateSianTransmissionUi() {
    const show = canTransmitSian();
    ['btn-export-qdca-log', 'btn-open-sian-portal', 'btn-open-sian-guide'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.toggle('hidden', !show);
    });
    updateSianFilterButton();
}

export function resetLogFilters() {
    const search = document.getElementById('log-search');
    const crop = document.getElementById('filter-crop');
    const activityType = document.getElementById('filter-activity-type');
    if (search) search.value = '';
    if (crop) crop.value = '';
    if (activityType) activityType.value = '';
    C.logSianPendingFilter = false;
    updateSianFilterButton();
}

export function hasActiveLogFilters() {
    const searchVal = (document.getElementById('log-search')?.value || '').trim();
    const cropFilter = document.getElementById('filter-crop')?.value || '';
    const activityTypeFilter = document.getElementById('filter-activity-type')?.value || '';
    return Boolean(searchVal || cropFilter || activityTypeFilter || C.logSianPendingFilter);
}

function activityMatchesLogFilters(t, searchVal, cropFilter, activityTypeFilter) {
    const activityCrop = getActivityCrop(t);
    const fieldName = (t.fieldName || '').toLowerCase();
    const matchesSearch = !searchVal ||
        fieldName.includes(searchVal) ||
        (t.product && t.product.toLowerCase().includes(searchVal)) ||
        (t.description && t.description.toLowerCase().includes(searchVal)) ||
        (t.adversity && t.adversity.toLowerCase().includes(searchVal)) ||
        (t.activityType && t.activityType.toLowerCase().includes(searchVal)) ||
        (activityCrop && activityCrop.toLowerCase().includes(searchVal));
    const matchesCrop = cropFilter === '' || activityCrop === cropFilter;
    const effectiveType = t.activityType || (t.product ? 'trattamento' : '');
    const matchesType = activityTypeFilter === '' || effectiveType === activityTypeFilter;
    const matchesSian = !C.logSianPendingFilter || !t.sianSyncId;
    return matchesSearch && matchesCrop && matchesType && matchesSian;
}

export function getFilteredLogTreatments() {
    const searchVal = (document.getElementById('log-search')?.value || '').toLowerCase().trim();
    const cropFilter = document.getElementById('filter-crop')?.value || '';
    const activityTypeFilter = document.getElementById('filter-activity-type')?.value || '';
    return (C.state.treatments || []).filter(t =>
        activityMatchesLogFilters(t, searchVal, cropFilter, activityTypeFilter)
    );
}

export function initTreatmentForm() {
    // Autofill treatment date with today
    document.getElementById('treatment-date').valueAsDate = new Date();

    // Toggle activity sections
    const activitySelect = document.getElementById('activity-type');
    activitySelect.addEventListener('change', () => {
        const selected = activitySelect.value;
        document.querySelectorAll('.activity-section').forEach(sec => {
            sec.classList.add('hidden');
        });
        document.getElementById(`sec-${selected}`).classList.remove('hidden');
        
        // Dynamically toggle required attribute for fields inside sections
        const treatmentTypeSelect = document.getElementById('treatment-type');
        if (selected === 'trattamento') {
            treatmentTypeSelect.setAttribute('required', 'required');
        } else {
            treatmentTypeSelect.removeAttribute('required');
        }
    });

    // Trigger dosage calculations when parameters change
    document.getElementById('treatment-field').addEventListener('change', updateDosageCalculator);
    document.getElementById('calc-dose-ha').addEventListener('input', updateDosageCalculator);
    document.getElementById('calc-water-ha').addEventListener('input', updateDosageCalculator);

    // Product select autofill based on chosen product
    const productInput = document.getElementById('treatment-product');
    productInput.addEventListener('change', (e) => {
        const productName = e.target.value.trim();
        const matchedProduct = C.state.inventory.find(p => p.name.toLowerCase() === productName.toLowerCase());
        
        if (matchedProduct) {
            document.getElementById('treatment-category').value = matchedProduct.category;
            document.getElementById('unit-product-used').textContent = matchedProduct.unit;
            
            // Set default carenza times
            if (matchedProduct.category === 'Fungicida') {
                document.getElementById('treatment-safety-days').value = 20;
            } else if (matchedProduct.category === 'Insetticida') {
                document.getElementById('treatment-safety-days').value = 7;
            } else {
                document.getElementById('treatment-safety-days').value = 0;
            }
            updateDosageCalculator();
        }
    });

    // Form submission
    document.getElementById('treatment-form').addEventListener('submit', (e) => {
        e.preventDefault();
        saveNewTreatment();
    });
}

export function populateTreatmentFieldOptions() {
    const select = document.getElementById('treatment-field');
    select.innerHTML = '<option value="">Seleziona campo...</option>';
    
    C.state.fields.forEach(field => {
        const opt = document.createElement('option');
        opt.value = field.id;
        opt.textContent = `${field.name} (${field.crop} - ${field.area.toFixed(2)} ha)`;
        select.appendChild(opt);
    });
}

export function populateTreatmentProductOptions() {
    const select = document.getElementById('treatment-product');
    if (!select) return;
    
    const currentVal = select.value;
    select.innerHTML = '<option value="">Seleziona un prodotto in magazzino...</option>';
    
    C.state.inventory.forEach(item => {
        const opt = document.createElement('option');
        opt.value = item.name;
        opt.textContent = `${item.name} (${item.stock} ${item.unit} disp.)`;
        select.appendChild(opt);
    });
    
    if (currentVal) {
        select.value = currentVal;
    }
}

export function updateDosageCalculator() {
    const fieldId = document.getElementById('treatment-field').value;
    const doseHa = parseFloat(document.getElementById('calc-dose-ha').value);
    const waterHa = parseFloat(document.getElementById('calc-water-ha').value);
    
    if (!fieldId || isNaN(doseHa) || isNaN(waterHa)) {
        document.getElementById('calc-result-text').textContent = 'Seleziona un campo e inserisci i parametri per calcolare la miscela.';
        return;
    }

    const field = C.state.fields.find(f => f.id === fieldId);
    if (!field) return;

    const totalProduct = (field.area * doseHa).toFixed(2);
    const totalWater = Math.round(field.area * waterHa);
    
    const matchedProductInput = document.getElementById('treatment-product').value.trim();
    const matchedProduct = C.state.inventory.find(p => p.name.toLowerCase() === matchedProductInput.toLowerCase());
    const unit = matchedProduct ? matchedProduct.unit : 'kg';

    document.getElementById('calc-result-text').innerHTML = `
        Per trattare il campo <strong>${field.name}</strong> (${field.area.toFixed(2)} ha):<br>
        &bull; Quantità: prodotto totale: <strong>${totalProduct} ${unit}</strong><br>
        &bull; Volume acqua totale: <strong>${totalWater} Litri</strong>
    `;

    // Fill form input values
    document.getElementById('treatment-quantity').value = totalProduct;
    document.getElementById('treatment-water').value = totalWater;
}

export function saveNewTreatment() {
    if (C.currentUser && C.currentUser.isDemo) {
        showRegisterRequiredAlert('registrare attività  nel quaderno di campagna');
        return;
    }
    const fieldId = document.getElementById('treatment-field').value;
    const date = document.getElementById('treatment-date').value;
    const activityType = document.getElementById('activity-type').value;

    if (!fieldId || !date || !activityType) {
        alert('Compilare i campi principali (Data, Campo, Tipo Attività ).');
        return;
    }

    // Controllo basato sui ruoli
    if (activityType === 'trattamento') {
        const userRole = C.currentUser ? C.currentUser.role.toLowerCase() : '';
        if (!C.currentUser || (userRole !== 'fitosanitario' && userRole !== 'admin')) {
            alert('La registrazione di trattamenti fitosanitari è riservata agli operatori Fitosanitari con brevetto valido. Modifica il tuo ruolo dal profilo.');
            const profileBtn = document.getElementById('user-profile-btn');
            if (profileBtn) profileBtn.click();
            return;
        }
        if (!C.currentUser.patent) {
            alert('Numero di brevetto obbligatorio per registrare trattamenti fitosanitari.');
            const profileBtn = document.getElementById('user-profile-btn');
            if (profileBtn) profileBtn.click();
            return;
        }
    }

    const field = C.state.fields.find(f => f.id === fieldId);
    if (!field) return;

    let product = '';
    let category = '';
    let adversity = '';
    let safetyDays = 0;
    let quantity = 0;
    let water = 0;
    let expiryDate = 'Nessuno';
    let description = '';
    let details = '';

    if (activityType === 'trattamento') {
        product = document.getElementById('treatment-product').value.trim();
        category = document.getElementById('treatment-category').value || 'Fitosanitario';
        adversity = document.getElementById('treatment-adversity').value.trim();
        safetyDays = parseInt(document.getElementById('treatment-safety-days').value) || 0;
        quantity = parseFloat(document.getElementById('treatment-quantity').value) || 0;
        water = parseInt(document.getElementById('treatment-water').value) || 0;

        if (!product || !adversity) {
            alert('Compilare tutti i campi del trattamento fitosanitario.');
            return;
        }

        // Expiry date calculation
        if (safetyDays > 0) {
            const d = parseDateRobust(date);
            if (d) {
                d.setDate(d.getDate() + safetyDays);
                expiryDate = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
            }
        }

        const treatmentTypeVal = document.getElementById('treatment-type').value || 'Generico';
        description = `Trattamento fitosanitario (${treatmentTypeVal})`;
        details = `Prodotto: ${product}\nAvversità:: ${adversity}\nDose: ${quantity} ${getUnitForProduct(product)} | Volume: ${water} L\nOperatore: ${C.currentUser.role} (Patent: ${C.currentUser.patent || 'N.D.'})\nFine carenza: ${expiryDate === 'Nessuno' ? 'Nessuna' : formatDate(expiryDate)}`;

    } else if (activityType === 'lavorazione') {
        const workType = document.getElementById('work-type').value;
        const workDepth = document.getElementById('work-depth').value;
        const workEquipment = document.getElementById('work-equipment').value.trim();

        description = `Lavorazione del terreno: ${workType}`;
        details = `Profondità: ${workDepth ? workDepth + ' cm' : 'Non specificata'}. Attrezzatura: ${workEquipment || 'Nessuna specificata'}`;
        product = workType; // For sorting/filtering

    } else if (activityType === 'concimazione') {
        const fertilizerName = document.getElementById('fertilizer-name').value.trim();
        const fertilizerType = document.getElementById('fertilizer-type').value;
        const fertilizerQty = parseFloat(document.getElementById('fertilizer-qty').value) || 0;
        const fertilizerNpk = document.getElementById('fertilizer-npk').value.trim();

        if (!fertilizerName) {
            alert('Inserisci il nome del concime.');
            return;
        }

        description = `Concimazione con ${fertilizerName}`;
        details = `Tipo: ${fertilizerType}, Quantità: ${fertilizerQty} kg, NPK: ${fertilizerNpk || 'N.D.'}`;
        product = fertilizerName;

    } else if (activityType === 'semina') {
        const sowingVariety = document.getElementById('sowing-variety').value.trim();
        const sowingQty = parseFloat(document.getElementById('sowing-qty').value) || 0;
        const sowingBatch = document.getElementById('sowing-batch').value.trim();

        if (!sowingVariety) {
            alert('Inserisci la varietà seminata o trapiantata.');
            return;
        }

        description = `Semina / Trapianto: ${sowingVariety}`;
        details = `Quantità: ${sowingQty} kg/piantine, Lotto: ${sowingBatch || 'N.D.'}`;
        product = sowingVariety;

    } else if (activityType === 'irrigazione') {
        const irrigationMethod = document.getElementById('irrigation-method').value;
        const irrigationVolume = parseFloat(document.getElementById('irrigation-volume').value) || 0;
        const irrigationDuration = parseFloat(document.getElementById('irrigation-duration').value) || 0;

        description = `Irrigazione (${irrigationMethod})`;
        details = `Volume d'acqua: ${irrigationVolume} Litri/mc, Durata: ${irrigationDuration} ore`;
        product = irrigationMethod;

    } else if (activityType === 'raccolta') {
        const harvestQty = parseFloat(document.getElementById('harvest-qty').value) || 0;
        const harvestDest = document.getElementById('harvest-dest').value.trim();

        description = `Raccolta raccolto`;
        details = `Quantità: ${harvestQty} q, Destinazione: ${harvestDest || 'Non specificata'}`;
        product = 'Raccolta';
    }

    if (activityType === 'trattamento') {
        const warehouseProduct = C.state.inventory.find(p => p.name.toLowerCase() === product.toLowerCase());
        if (warehouseProduct && warehouseProduct.stock < quantity) {
            if (!confirm(`Attenzione! La quantità in magazzino (${warehouseProduct.stock} ${warehouseProduct.unit}) è inferiore a quella necessaria (${quantity} ${warehouseProduct.unit}). Vuoi comunque registrare il trattamento?`)) {
                return;
            }
        }
    }

    let targetCampaignYear = C.state.currentCampaignYear;
    if (C.companyInfo && C.companyInfo.split_campaigns === 1 && C.state.campaigns) {
        // Smart routing based on crop type
        const isPermanent = isPermanentCrop(field.crop);
        const targetType = isPermanent ? 'agrario' : 'solare';
        // Try to find an open campaign of the target type
        const match = C.state.campaigns.find(c => c.status === 'aperta' && c.year_type === targetType);
        if (match) {
            targetCampaignYear = match.year;
        }
    }

    const newActivity = {
        date: date,
        fieldId: field.id,
        fieldName: field.name,
        fieldCrop: field.crop,
        activityType: activityType,
        product: product,
        category: category,
        adversity: adversity,
        safetyDays: safetyDays,
        quantity: quantity,
        water: water,
        expiryDate: expiryDate,
        description: description,
        details: details,
        campaignYear: targetCampaignYear
    };

    // Verifica di sicurezza: controlla se il terreno è attualmente in tempo di carenza
    const carenzaCheck = checkFieldSafetyRestriction(field.id);
    if (carenzaCheck.isInRestriction) {
        const expiryStr = carenzaCheck.expiryDate.toLocaleDateString('it-IT');
        if (!confirm(`⚠️ AVVISO SICUREZZA CAMPO!\nIl terreno "${field.name}" è attualmente in TEMPO DI CARENZA fino al ${expiryStr} per l'impiego di "${carenzaCheck.product}".\n\nSei sicuro di voler registrare un'altra attività (${activityType}) su questo campo?`)) {
            return;
        }
    }

    const resetTreatmentForm = () => {
        document.getElementById('treatment-form').reset();
        document.querySelectorAll('.activity-section').forEach(sec => {
            sec.classList.add('hidden');
        });
        document.getElementById('sec-trattamento').classList.remove('hidden');
        document.getElementById('calc-result-text').textContent = 'Seleziona un campo e inserisci i parametri per calcolare la miscela.';
        switchTab('treatment-log');
    };

    api.saveActivity(newActivity)
        .then(async () => {
            if (!Array.isArray(C.state.treatments)) C.state.treatments = [];
            if (!Array.isArray(C.state.carenzeTreatments)) C.state.carenzeTreatments = [];
            C.state.treatments.unshift(newActivity);
            C.state.carenzeTreatments.unshift(newActivity);

            const warehouseProduct = C.state.inventory.find(p => p.name.toLowerCase() === product.toLowerCase());
            if (warehouseProduct) {
                const newStock = parseFloat((warehouseProduct.stock - quantity).toFixed(2));
                await api.updateStock(warehouseProduct.id, newStock).catch(() => {});
            }
            await loadApplicationData();
            resetTreatmentForm();
            alert('Attività registrata con successo nel quaderno di campagna.');
        })
        .catch(err => {
            console.warn('Errore di rete o connessione assente. Salvataggio in coda offline:', err);
            addActivityToOfflineQueue(newActivity);
            resetTreatmentForm();
            alert('🌐 Connessione assente o instabile: l\'attività è stata salvata OFFLINE sul dispositivo. Verrà trasmessa al server in automatico non appena tornerà la rete.');
        });
}

// --- SCANNER INTEGRATION (Camera & Barcode) ---

export function initScanner() {
    const toggleScannerBtn = document.getElementById('btn-toggle-scanner');
    const scannerWrapper = document.getElementById('scanner-wrapper');

    toggleScannerBtn.addEventListener('click', () => {
        if (scannerWrapper.classList.contains('hidden')) {
            scannerWrapper.classList.remove('hidden');
            toggleScannerBtn.innerHTML = '<i class="fa-solid fa-camera-rotate"></i> Ferma Scanner';
            startBarcodeScanner("scanner-preview", (decodedText) => {
                handleScannedBarcode(decodedText);
                stopBarcodeScanner();
                scannerWrapper.classList.add('hidden');
                toggleScannerBtn.innerHTML = '<i class="fa-solid fa-camera"></i> Avvia Scanner Barcode';
            });
        } else {
            stopBarcodeScanner();
            scannerWrapper.classList.add('hidden');
            toggleScannerBtn.innerHTML = '<i class="fa-solid fa-camera"></i> Avvia Scanner Barcode';
        }
    });

    const toggleInvScannerBtn = document.getElementById('btn-toggle-inv-scanner');
    const invScannerWrapper = document.getElementById('inv-scanner-wrapper');

    if (toggleInvScannerBtn) {
        toggleInvScannerBtn.addEventListener('click', () => {
            if (invScannerWrapper.classList.contains('hidden')) {
                invScannerWrapper.classList.remove('hidden');
                toggleInvScannerBtn.innerHTML = '<i class="fa-solid fa-camera-rotate"></i>';
                startBarcodeScanner("inv-scanner-preview", (decodedText) => {
                    document.getElementById('inv-barcode').value = decodedText;
                    stopBarcodeScanner();
                    invScannerWrapper.classList.add('hidden');
                    toggleInvScannerBtn.innerHTML = '<i class="fa-solid fa-camera"></i>';
                });
            } else {
                stopBarcodeScanner();
                invScannerWrapper.classList.add('hidden');
                toggleInvScannerBtn.innerHTML = '<i class="fa-solid fa-camera"></i>';
            }
        });
    }

    // Mock buttons triggers for demo
    document.querySelectorAll('.btn-mock').forEach(btn => {
        btn.addEventListener('click', () => {
            const barcode = btn.getAttribute('data-barcode');
            handleScannedBarcode(barcode);
        });
    });
}

export function startBarcodeScanner(elementId, successCallback) {
    if (C.html5QrcodeScanner) {
        C.html5QrcodeScanner.stop().catch(() => {});
    }
    C.html5QrcodeScanner = new Html5Qrcode(elementId);
    
    const config = { 
        fps: 10, 
        qrbox: { width: 250, height: 120 } // Wide box for barcodes
    };

    const attemptStart = (facingModeObj) => {
        C.html5QrcodeScanner.start(
            facingModeObj, 
            config,
            successCallback,
            (errorMessage) => {
                // Ignore noisy frame errors
            }
        ).catch(err => {
            console.warn(`Fallito avvio fotocamera con ${JSON.stringify(facingModeObj)}: `, err);
            if (facingModeObj.facingMode === "environment") {
                console.log("Provo ad avviare la fotocamera frontale (webcam pc)...");
                attemptStart({ facingMode: "user" });
            } else {
                alert("Impossibile accedere alla fotocamera. Controlla i permessi del browser.");
            }
        });
    };

    attemptStart({ facingMode: "environment" });
}

export function stopBarcodeScanner() {
    if (C.html5QrcodeScanner) {
        C.html5QrcodeScanner.stop().then(() => {
            C.html5QrcodeScanner = null;
        }).catch(err => console.error("Error stopping scanner: ", err));
    }
}

export function handleScannedBarcode(barcode) {
    // Look up product in inventory
    const product = C.state.inventory.find(p => p.barcode === barcode);
    
    if (product) {
        document.getElementById('treatment-product').value = product.name;
        document.getElementById('treatment-category').value = product.category;
        document.getElementById('unit-product-used').textContent = product.unit;

        // Carenza days autofill defaults
        if (product.category === 'Fungicida') {
            document.getElementById('treatment-safety-days').value = 20;
        } else if (product.category === 'Insetticida') {
            document.getElementById('treatment-safety-days').value = 7;
        } else {
            document.getElementById('treatment-safety-days').value = 0;
        }

        // Trigger UI updates
        updateDosageCalculator();
        alert(`Prodotto riconosciuto con successo: ${product.name} (${product.category})`);
    } else {
        alert(`Codice a barre scansionato: ${barcode}\nNessun prodotto abbinato trovato nel magazzino. Puoi aggiungerlo manualmente.`);
        document.getElementById('treatment-product').value = '';
    }
}

// --- WAREHOUSE / INVENTORY MANAGEMENT ---
export function initInventory() {
    document.getElementById('add-inventory-form').addEventListener('submit', (e) => {
        e.preventDefault();
        saveNewInventoryProduct();
    });
}

export function renderInventoryTable() {
    populateTreatmentProductOptions();
    const tbody = document.getElementById('inventory-table-body');
    tbody.innerHTML = '';

    if (C.state.inventory.length === 0) {
        tbody.innerHTML = `<tr class="empty-row"><td colspan="6">Nessun prodotto a magazzino. Carica il tuo primo prodotto.</td></tr>`;
        return;
    }

    C.state.inventory.forEach(p => {
        const isLow = p.stock <= 5; // Alert logic for low stocks
        const badgeClass = isLow ? 'badge-danger' : 'badge-success';
        const badgeLabel = isLow ? 'Scorte Basse' : 'Disponibile';

        const row = document.createElement('tr');
        row.innerHTML = `
            <td><strong>${p.name}</strong></td>
            <td>${p.category}</td>
            <td>${p.active_ingredient || p.activeIngredient || ''}</td>
            <td><code>${p.barcode || 'Non inserito'}</code></td>
            <td>${p.stock} ${p.unit}</td>
            <td style="display: flex; gap: 8px; align-items: center;">
                <span class="badge ${badgeClass}">${badgeLabel}</span>
                <button class="btn-icon btn-delete-inventory" data-id="${p.id}" title="Elimina Prodotto">
                    <i class="fa-solid fa-trash-can" style="color: #f85149;"></i>
                </button>
            </td>
        `;
        tbody.appendChild(row);
    });

    // Aggiungi listener per eliminazione
    document.querySelectorAll('.btn-delete-inventory').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            const id = e.currentTarget.dataset.id;
            if (C.currentUser && C.currentUser.isDemo) {
                showRegisterRequiredAlert('cancellare i prodotti dal magazzino');
                return;
            }
            if (confirm('Sei sicuro di voler rimuovere questo prodotto dal magazzino?')) {
                try {
                    await api.deleteInventoryItem(id);
                    await loadApplicationData();
                    alert('Prodotto rimosso.');
                } catch (err) {
                    alert('Errore nella rimozione: ' + err.message);
                }
            }
        });
    });
}

export function saveNewInventoryProduct() {
    if (C.currentUser && C.currentUser.isDemo) {
        showRegisterRequiredAlert('aggiungere prodotti al magazzino');
        return;
    }
    const name = document.getElementById('inv-name').value.trim();
    const category = document.getElementById('inv-category').value;
    const activeIngredient = document.getElementById('inv-active-ingredient').value.trim();
    const barcode = document.getElementById('inv-barcode').value.trim();
    const stock = parseFloat(document.getElementById('inv-stock').value);

    if (!name || !activeIngredient || isNaN(stock)) {
        alert('Compilare tutti i campi obbligatori.');
        return;
    }

    const newProduct = {
        id: 'inv_' + Date.now(),
        name: name,
        category: category,
        activeIngredient: activeIngredient,
        barcode: barcode || ('ean_' + Date.now()),
        stock: stock,
        unit: category === 'Concime' || category === 'Fungicida' ? 'kg' : 'L'
    };

    api.saveInventoryItem(newProduct)
        .then(async () => {
            await loadApplicationData();
            document.getElementById('add-inventory-form').reset();
            alert('Prodotto aggiunto correttamente al magazzino.');
        })
        .catch(err => {
            alert('Errore nell\'aggiunta del prodotto: ' + err.message);
        });
}

// --- LOGS & RENDERING ---
export function renderRecentTreatments() {
    const tbody = document.querySelector('#recent-treatments-table tbody');
    tbody.innerHTML = '';

    const list = [...C.state.treatments].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

    if (list.length === 0) {
        tbody.innerHTML = `<tr class="empty-row"><td colspan="5">Nessuna attività registrata. Aggiungi la tua prima operazione!</td></tr>`;
        return;
    }

    list.forEach(t => {
        const typeLabel = t.activityType ? t.activityType.toUpperCase() : 'TRATTAMENTO';
        const descText = t.description || t.product;
        const row = document.createElement('tr');
        row.innerHTML = `
            <td>${formatDate(t.date)}</td>
            <td>${t.fieldName}</td>
            <td>${t.fieldCrop}</td>
            <td><span class="badge badge-warning">${typeLabel}</span></td>
            <td>${descText}</td>
        `;
        tbody.appendChild(row);
    });
}


export function populateLogCropFilter() {
    const select = document.getElementById('filter-crop');
    if (!select) return;

    const current = select.value;
    const crops = new Set();
    (C.state.fields || []).forEach(f => {
        if (f.crop) crops.add(f.crop);
    });

    select.innerHTML = '<option value="">Tutte le colture (mappa)</option>';
    if (crops.size === 0) {
        select.title = 'Aggiungi campi in mappa per filtrare per coltura';
        return;
    }

    select.title = 'Colture dei campi attualmente registrati in mappa';
    [...crops].sort((a, b) => a.localeCompare(b, 'it')).forEach(crop => {
        const count = C.state.fields.filter(f => f.crop === crop).length;
        const opt = document.createElement('option');
        opt.value = crop;
        opt.textContent = count > 1 ? `${crop} (${count} campi)` : crop;
        select.appendChild(opt);
    });

    if (current && crops.has(current)) {
        select.value = current;
    }
}

export function renderFullLogTable() {
    const tbody = document.getElementById('log-table-body');
    if (!tbody) return;
    tbody.innerHTML = '';

    const filtered = getFilteredLogTreatments();

    // Sort by date desc
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));

    if (filtered.length === 0) {
        const total = (C.state.treatments || []).length;
        const filtersActive = hasActiveLogFilters();
        if (total > 0 && filtersActive) {
            tbody.innerHTML = `<tr class="empty-row"><td colspan="7">
                ${total} attività nel registro, ma nascoste dai filtri attivi.
                <button type="button" class="btn btn-secondary btn-sm" id="btn-reset-log-filters" style="margin-left:8px;">Rimuovi filtri</button>
            </td></tr>`;
            document.getElementById('btn-reset-log-filters')?.addEventListener('click', () => {
                resetLogFilters();
                renderFullLogTable();
            });
        } else {
            const emptyMsg = C.logSianPendingFilter
                ? 'Nessuna attività da trasmettere al SIAN corrispondente ai filtri.'
                : total === 0
                    ? 'Nessuna attività in archivio. Registra aratura, concimazione, trattamenti e altre operazioni dal menu «Nuovo Trattamento».'
                    : 'Nessuna attività trovata corrispondente ai filtri.';
            tbody.innerHTML = `<tr class="empty-row"><td colspan="7">${emptyMsg}</td></tr>`;
        }
        updateSianFilterButton();
        return;
    }

    filtered.forEach(t => {
        const activityCrop = getActivityCrop(t);
        const typeLabel = t.activityType ? t.activityType.charAt(0).toUpperCase() + t.activityType.slice(1) : 'Trattamento';
        const descText = t.description || t.product;
        const detailsText = t.details || `Dose: ${t.quantity} ${getUnitForProduct(t.product)}, Acqua: ${t.water}L`;
        
        // Verifica stato trasmissione SIAN
        const isSynced = t.sianSyncId;
        const syncBadge = isSynced
            ? `<div style="margin-top: 4px; color: #3fb950; font-size: 11px;"><i class="fa-solid fa-circle-check"></i> Trasmesso al SIAN</div>`
            : `<div style="margin-top: 4px; color: #e3b341; font-size: 11px;"><i class="fa-solid fa-cloud-arrow-up"></i> Da trasmettere</div>`;

        const canMark = canTransmitSian() && !isSynced;
        const row = document.createElement('tr');
        row.innerHTML = `
            <td>${formatDate(t.date)}</td>
            <td>${t.fieldName}</td>
            <td>${activityCrop || t.fieldCrop || '—'}</td>
            <td>
                <span class="badge badge-success">${typeLabel}</span>
                ${syncBadge}
            </td>
            <td><strong>${descText}</strong></td>
            <td><small style="white-space: pre-line;">${detailsText}</small></td>
            <td>
                <div style="display: flex; gap: 8px; justify-content: center; align-items: center;">
                    ${canMark ? `
                        <button class="btn-icon btn-mark-sian" data-id="${t.id}" title="Segna come trasmesso al SIAN" style="color: #3fb950;">
                            <i class="fa-solid fa-check-double"></i>
                        </button>
                    ` : ''}
                    <button class="btn-icon btn-delete-activity" data-id="${t.id}" title="Elimina Attività">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>
            </td>
        `;
        
        if (canMark) {
            const markBtn = row.querySelector('.btn-mark-sian');
            if (markBtn) {
                markBtn.addEventListener('click', () => markActivitySianTransmitted(t.id, markBtn));
            }
        }

        // Handler per cancellazione
        row.querySelector('.btn-delete-activity').addEventListener('click', () => {
            if (C.currentUser && C.currentUser.isDemo) {
                showRegisterRequiredAlert('eliminare attività dal registro');
                return;
            }
            if (confirm('Eliminare definitivamente questa attività dal registro storico? (Le scorte a magazzino non verranno riaccreditate).')) {
                api.deleteActivity(t.id)
                    .then(() => {
                        loadApplicationData();
                    })
                    .catch(err => alert('Errore nell\'eliminazione dell\'attività : ' + err.message));
            }
        });

        tbody.appendChild(row);
    });

    updateSianFilterButton();

    // Filter event listeners (setup once)
    if (!window.logFilterInitialized) {
        document.getElementById('log-search').addEventListener('input', renderFullLogTable);
        document.getElementById('filter-crop').addEventListener('change', renderFullLogTable);
        const activityTypeFilter = document.getElementById('filter-activity-type');
        if (activityTypeFilter) {
            activityTypeFilter.addEventListener('change', renderFullLogTable);
        }
        const sianFilterBtn = document.getElementById('btn-filter-sian-pending');
        if (sianFilterBtn) {
            sianFilterBtn.addEventListener('click', () => {
                C.logSianPendingFilter = !C.logSianPendingFilter;
                renderFullLogTable();
            });
        }
        window.logFilterInitialized = true;
    }
}

export async function checkAndNotifyReminders() {
    if (!C.state.treatments || C.state.treatments.length === 0) {
        const banner = document.getElementById('dashboard-notifications-banner');
        if (banner) banner.classList.add('hidden');
        return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const notifications = [];
    const pushQueue = [];

    if (!window._notifiedKeys) {
        window._notifiedKeys = new Set();
    }

    for (const t of C.state.treatments) {
        const treatDate = parseDateRobust(t.date);
        if (!treatDate) continue;
        treatDate.setHours(0, 0, 0, 0);

        // 1. NOTIFICA: Termine invio SIAN (30 giorni dal trattamento) — per chi può trasmettere
        const isSynced = t.sian_sync_id || t.sianSyncId;
        if (!isSynced && canTransmitSian()) {
            const diffTime = Math.abs(today - treatDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays >= 20) {
                const daysRemaining = 30 - diffDays;
                let text = '';
                let urgency = 'warning';
                if (daysRemaining > 0) {
                    text = `⏳ <strong>Termine SIAN vicino</strong>: Il trattamento del ${formatDate(t.date)} sul campo "${t.fieldName || 'N.D.'}" con ${t.product} non è ancora trasmesso. Mancano <strong>${daysRemaining} giorni</strong> al limite di 30. Esporta QDCA e carica sul portale SIAN.`;
                } else {
                    text = `🚨 <strong>Termine SIAN scaduto</strong>: Il trattamento del ${formatDate(t.date)} sul campo "${t.fieldName || 'N.D.'}" con ${t.product} ha superato il limite di 30 giorni per l'invio (passati ${diffDays} giorni).`;
                    urgency = 'danger';
                }
                notifications.push({ id: `sian-${t.id}`, text, type: urgency });

                const key = `sian-${t.id}`;
                if (!window._notifiedKeys.has(key)) {
                    pushQueue.push({
                        title: daysRemaining > 0 ? 'Scadenza Trasmissione SIAN' : 'Trasmissione SIAN Scaduta',
                        body: daysRemaining > 0 ? `Mancano ${daysRemaining} giorni per trasmettere il trattamento del ${formatDate(t.date)}.` : `Trattamento del ${formatDate(t.date)} non trasmesso entro i 30 giorni.`
                    });
                    window._notifiedKeys.add(key);
                }
            }
        }

        // 2. NOTIFICA 2: Carenza finita
        const sDays = parseInt(t.safetyDays ?? t.safety_days ?? t.tempoCarenzaGiorni ?? t.tempo_carenza_giorni) || 0;
        if (sDays > 0) {
            const exp = new Date(treatDate);
            exp.setDate(exp.getDate() + sDays);
            exp.setHours(0, 0, 0, 0);

            const diffTimeExp = today - exp;
            const diffDaysExp = Math.ceil(diffTimeExp / (1000 * 60 * 60 * 24));
            // Se la carenza è finita (exp <= today) ed è finita di recente (es. negli ultimi 10 giorni)
            if (exp <= today && diffDaysExp <= 10) {
                const text = `🌱 <strong>Carenza finita</strong>: Il periodo di carenza per il trattamento ${t.product} su "${t.fieldName || 'N.D.'}" è terminato il <strong>${formatDate(exp)}</strong>. Il raccolto è ora sicuro!`;
                notifications.push({ id: `carenza-finita-${t.id}`, text, type: 'success' });

                const key = `carenza-finita-${t.id}`;
                if (!window._notifiedKeys.has(key)) {
                    pushQueue.push({
                        title: 'Periodo di Carenza Terminato',
                        body: `La carenza per il trattamento ${t.product} sul campo ${t.fieldName || 'N.D.'} è terminata. Il raccolto è ora sicuro.`
                    });
                    window._notifiedKeys.add(key);
                }
            }
        }
    }

    // Aggiorna l'interfaccia utente (UI)
    const banner = document.getElementById('dashboard-notifications-banner');
    const list = document.getElementById('dashboard-notifications-list');
    
    if (notifications.length > 0 && banner && list) {
        list.innerHTML = '';
        notifications.forEach(n => {
            const li = document.createElement('li');
            li.style.padding = '6px 0';
            li.style.borderBottom = '1px solid rgba(255,255,255,0.04)';
            li.style.listStyle = 'none';
            
            let icon = '⏳';
            let color = '#fbbf24'; // Giallo/Ambra
            if (n.type === 'danger') {
                icon = '🚨';
                color = '#f87171'; // Rosso
            } else if (n.type === 'success') {
                icon = '✅';
                color = '#4ade80'; // Verde
            }
            
            li.innerHTML = `<span style="color:${color}; display: flex; align-items: flex-start; gap: 8px;"><span>${icon}</span> <span>${n.text}</span></span>`;
            list.appendChild(li);
        });
        banner.classList.remove('hidden');
    } else if (banner) {
        banner.classList.add('hidden');
    }

    // Trigger delle Notifiche Desktop del Browser
    if (pushQueue.length > 0 && 'Notification' in window) {
        if (Notification.permission === 'granted') {
            pushQueue.forEach(p => new Notification(p.title, { body: p.body, icon: 'logo.png' }));
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    pushQueue.forEach(p => new Notification(p.title, { body: p.body, icon: 'logo.png' }));
                }
            });
        }
    }
}

export function updateDashboardStats() {
    const fields = C.state.fields || [];
    const inventory = C.state.inventory || [];

    const fieldsEl = document.getElementById('stat-fields-count');
    const haEl = document.getElementById('stat-total-ha');
    const carenzeEl = document.getElementById('stat-pending-treatments');
    const productsEl = document.getElementById('stat-products-count');

    if (fieldsEl) fieldsEl.textContent = fields.length;

    if (haEl) {
        const totalHa = fields.reduce((acc, curr) => acc + (curr.area || 0), 0);
        haEl.textContent = `${totalHa.toFixed(2)} ha`;
    }

    if (carenzeEl) {
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const carenzeList = getTreatmentCarenzeList();
            const activeCarenze = carenzeList.filter(t => {
                const sDays = parseInt(t.safetyDays ?? t.safety_days ?? t.tempoCarenzaGiorni ?? t.tempo_carenza_giorni) || 0;
                if (sDays <= 0 || !t.date) return false;
                const treatDate = parseDateRobust(t.date);
                if (!treatDate) return false;
                treatDate.setHours(0, 0, 0, 0);
                const exp = new Date(treatDate);
                exp.setDate(exp.getDate() + sDays);
                return exp >= today;
            }).length;
            carenzeEl.textContent = activeCarenze;
        } catch (e) {
            console.error('[AgriLog] updateDashboardStats carenze:', e);
            carenzeEl.textContent = '0';
        }
    }

    if (productsEl) productsEl.textContent = inventory.length;

    try {
        checkAndNotifyReminders();
    } catch (e) {
        console.error('[AgriLog] updateDashboardStats notifications:', e);
    }
}

export function refreshDashboardView() {
    renderRecentTreatments();
    updateDashboardStats();
}

// --- PDF & EXCEL EXPORTS ---
export function initExport() {
    document.getElementById('btn-export-pdf').addEventListener('click', generatePDFReport);
    document.getElementById('btn-export-excel').addEventListener('click', generateExcelMockReport);
    const btnExportQdcaLog = document.getElementById('btn-export-qdca-log');
    if (btnExportQdcaLog) {
        btnExportQdcaLog.addEventListener('click', () => {
            if (!canTransmitSian()) {
                alert('L\'export QDCA è riservato ad amministratori e professionisti abilitati (agronomo, agrotecnico, perito agrario).');
                return;
            }
            generateQdcaXml();
        });
    }
    const btnInventoryPdf = document.getElementById('btn-export-inventory-pdf');
    if (btnInventoryPdf) btnInventoryPdf.addEventListener('click', generateInventoryPDFReport);
}

export function generateInventoryPDFReport() {
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF('portrait');

    // Draw header and border styling
    doc.setFillColor(13, 17, 23); // Dark slate header background
    doc.rect(0, 0, 210, 45, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("REGISTRO MAGAZZINO", 15, 20);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(139, 148, 158);
    doc.text("Giacenze di fitosanitari, diserbanti e fertilizzanti", 15, 30);
    
    const todayStr = new Date().toLocaleDateString('it-IT');
    doc.text(`Data Stampa: ${todayStr}`, 15, 38);

    // Build Table Data
    const tableHeaders = [
        ["Nome Prodotto", "Categoria", "Principio Attivo", "Codice a Barre", "Giacenza"]
    ];

    const sortedInventory = [...C.state.inventory].sort((a, b) => a.name.localeCompare(b.name));
    
    const tableRows = sortedInventory.map(p => {
        return [
            p.name,
            p.category,
            p.active_ingredient || p.activeIngredient || 'N.D.',
            p.barcode || 'N.D.',
            `${p.stock} ${p.unit}`
        ];
    });

    // Generate table in PDF using autoTable plugin
    doc.autoTable({
        startY: 55,
        head: tableHeaders,
        body: tableRows,
        theme: 'grid',
        headStyles: {
            fillColor: [10, 132, 255],
            textColor: [255, 255, 255],
            fontStyle: 'bold'
        },
        styles: {
            fontSize: 10,
            cellPadding: 4,
            textColor: [40, 40, 40]
        },
        alternateRowStyles: {
            fillColor: [245, 248, 250]
        }
    });

    doc.save(`Magazzino_AgriLog_${todayStr.replace(/\//g, '-')}.pdf`);
}

export function generatePDFReport() {
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF('landscape'); // Landscape for rich columns representation

    // Draw header and border styling
    doc.setFillColor(13, 17, 23); // Dark slate header background
    doc.rect(0, 0, 297, 45, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("QUADERNO DI CAMPAGNA - REGISTRO DELLE ATTIVITÀ", 15, 20);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(139, 148, 158);
    doc.text("Ai sensi del D.P.R. 290/01 e s.m.i. e del PAN (Piano di Azione Nazionale) - Conforme SIAN", 15, 30);
    
    const todayStr = new Date().toLocaleDateString('it-IT');
    doc.text(`Data Stampa: ${todayStr} | Documento valido ai fini dei controlli PAC ed Ecoschemi`, 15, 38);

    // Farm Info
    doc.setTextColor(13, 17, 23);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("DATI AZIENDALI", 15, 55);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("Denominazione: Azienda Agricola di Nicola", 15, 62);
    doc.text("Codice Fiscale / P.IVA: IT01234567890", 15, 68);
    doc.text(`Campi attivi: ${C.state.fields.length} (Tot. ${C.state.fields.reduce((a,b)=>a+b.area, 0).toFixed(2)} ha)`, 15, 74);

    // Build Table Data
    const tableHeaders = [
        ["Data Int.", "Campo / Superficie", "Coltura", "Tipo Attività ", "Descrizione", "Dettagli Operativi"]
    ];

    const sortedTreatments = [...C.state.treatments].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    const tableRows = sortedTreatments.map(t => {
        const area = getFieldAreaFormatted(t.fieldId);
        const typeLabel = t.activityType ? t.activityType.toUpperCase() : 'TRATTAMENTO';
        const descText = t.description || t.product;
        const detailsText = t.details || `Prodotto: ${t.product}\nQuantità: ${t.quantity} ${getUnitForProduct(t.product)}\nAcqua: ${t.water} L\nCarenza: ${t.expiryDate === 'Nessuno' ? 'Nessuna' : formatDate(t.expiryDate)}`;
        
        return [
            formatDate(t.date),
            `${t.fieldName}\n(${area})`,
            t.fieldCrop,
            typeLabel,
            descText,
            detailsText
        ];
    });

    // Generate table in PDF using autoTable plugin
    doc.autoTable({
        startY: 82,
        head: tableHeaders,
        body: tableRows,
        theme: 'grid',
        headStyles: {
            fillColor: [46, 164, 79], // Green accent for headers
            textColor: [255, 255, 255],
            fontSize: 10,
            halign: 'center'
        },
        bodyStyles: {
            fontSize: 9,
            valign: 'middle'
        },
        alternateRowStyles: {
            fillColor: [245, 248, 250]
        },
        columnStyles: {
            0: { halign: 'center', cellWidth: 25 },
            1: { cellWidth: 45 },
            2: { cellWidth: 35 },
            3: { halign: 'center', cellWidth: 35 },
            4: { cellWidth: 60 },
            5: { cellWidth: 70 }
        }
    });

    // Save PDF
    doc.save(`registro_attivita_campagna_${new Date().toISOString().split('T')[0]}.pdf`);
}

export function generateExcelMockReport() {
    // Generate a simple CSV file with Excel-compatible UTF-8 BOM
    let csvContent = "\uFEFF"; // BOM
    csvContent += "Data Operazione;Campo;Superficie (ha);Coltura;Tipo Attività ;Descrizione;Dettagli Operativi\r\n";
    
    C.state.treatments.forEach(t => {
        const f = C.state.fields.find(field => field.id === t.fieldId);
        const areaStr = f ? f.area.toFixed(2) : '-';
        const typeLabel = t.activityType ? t.activityType.toUpperCase() : 'TRATTAMENTO';
        const descText = t.description || t.product;
        const detailsText = (t.details || `Prodotto: ${t.product}, Quantità: ${t.quantity}, Acqua: ${t.water}`).replace(/(\r\n|\n|\r)/gm, " ");
        
        csvContent += `${formatDate(t.date)};${t.fieldName};${areaStr};${t.fieldCrop};${typeLabel};${descText};${detailsText}\r\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `registro_attivita_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function initRotateHandlers() {
    const modal = document.getElementById('crop-rotate-modal');
    const form = document.getElementById('rotate-form');
    const closeBtn = document.getElementById('close-rotate-modal');
    const macroSelect = document.getElementById('rotate-macro-category');
    const piantagioniGroup = document.getElementById('rotate-crop-piantagioni-group');
    const campiGroup = document.getElementById('rotate-crop-campi-group');
    const piantagioniSelect = document.getElementById('rotate-crop-piantagioni');
    const campiSelect = document.getElementById('rotate-crop-campi');
    const customCropGroup = document.getElementById('rotate-crop-custom-group');
    const customCropInput = document.getElementById('rotate-crop-custom');

    // Toggle sub-category inputs
    macroSelect.addEventListener('change', () => {
        const val = macroSelect.value;
        piantagioniGroup.classList.add('hidden');
        campiGroup.classList.add('hidden');
        customCropGroup.classList.add('hidden');
        piantagioniSelect.required = false;
        campiSelect.required = false;
        customCropInput.required = false;

        if (val === 'Frutteti') {
            piantagioniGroup.classList.remove('hidden');
            piantagioniSelect.required = true;
        } else if (val === 'A campo aperto') {
            campiGroup.classList.remove('hidden');
            campiSelect.required = true;
        }
    });

    // Custom crop toggle
    [piantagioniSelect, campiSelect].forEach(select => {
        select.addEventListener('change', () => {
            if (select.value === 'Altro') {
                customCropGroup.classList.remove('hidden');
                customCropInput.required = true;
            } else {
                customCropGroup.classList.add('hidden');
                customCropInput.required = false;
            }
        });
    });

    // Close on X
    if (closeBtn) closeBtn.addEventListener('click', () => {
        modal.classList.remove('show');
        setTimeout(() => modal.style.display = 'none', 300);
    });

    // Close on click outside
    if (modal) {
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                modal.classList.remove('show');
                setTimeout(() => modal.style.display = 'none', 300);
            }
        });
    }

    // Submit
    form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (C.currentUser && C.currentUser.isDemo) {
            showRegisterRequiredAlert('registrare la rotazione coltura');
            return;
        }
        const fieldId = document.getElementById('rotate-field-id').value;
        const macro = macroSelect.value;
        let crop = '';

        if (macro === 'Frutteti') {
            crop = piantagioniSelect.value;
        } else {
            crop = campiSelect.value;
        }

        if (crop === 'Altro') {
            crop = customCropInput.value.trim() || 'Altro';
        }

        const newCropFormatted = `${macro}: ${crop}`;
        const fieldIndex = C.state.fields.findIndex(f => f.id === fieldId);

        if (fieldIndex !== -1) {
            const field = C.state.fields[fieldIndex];
            const oldCrop = field.crop;

            api.updateCrop(fieldId, newCropFormatted)
                .then(async () => {
                    const rotateActivity = {
                        date: new Date().toISOString().split('T')[0],
                        fieldId: field.id,
                        fieldName: field.name,
                        fieldCrop: newCropFormatted,
                        activityType: 'lavorazione',
                        product: 'Rotazione Coltura',
                        category: 'Rotazione',
                        adversity: '',
                        safetyDays: 0,
                        quantity: 0,
                        water: 0,
                        expiryDate: 'Nessuno',
                        description: `Rotazione coltura su ${field.name}`,
                        details: `Coltura precedente: ${oldCrop} -> Nuova coltura: ${newCropFormatted}`
                    };
                    
                    await api.saveActivity(rotateActivity);
                    
                    // Ricarica dati
                    await loadApplicationData();
                    
                    // Close modal
                    modal.classList.remove('show');
                    setTimeout(() => modal.style.display = 'none', 300);
                    
                    alert(`Coltura annata aggiornata con successo per ${field.name}!`);
                })
                .catch(err => {
                    alert('Errore nella registrazione della rotazione coltura: ' + err.message);
                });
        }
    });
}

export async function markActivitySianTransmitted(activityId, buttonEl) {
    if (C.currentUser && C.currentUser.isDemo) {
        showRegisterRequiredAlert('segnare attività come trasmesse al SIAN');
        return;
    }
    if (!confirm('Confermi di aver trasmesso questa attività al SIAN tramite il portale ufficiale?')) {
        return;
    }
    const originalHtml = buttonEl?.innerHTML;
    if (buttonEl) {
        buttonEl.disabled = true;
        buttonEl.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>';
    }
    try {
        await api.markSianTransmitted(activityId);
        await loadApplicationData();
    } catch (err) {
        alert('Errore: ' + err.message);
        if (buttonEl) {
            buttonEl.disabled = false;
            buttonEl.innerHTML = originalHtml;
        }
    }
}

export function syncActivityWithSian(activityId, buttonEl) {
    alert('Sincronizzazione automatica SIAN in stand-by.\n\nEsporta il file QDCA dal Registro e caricalo sul portale SIAN con le credenziali del professionista. Poi usa "Segna come trasmesso".');
}

// ----- CAMPAIGN YEAR MANAGEMENT -----

export async function initCampaignYear() {
    try {
        C.state.campaigns = await api.getCampaigns();
    } catch(e) {
        C.state.campaigns = [];
    }

    // Se non c'è nessuna campagna, crea quella dell'anno corrente
    if (C.state.campaigns.length === 0) {
        const y = new Date().getFullYear().toString();
        try {
            await api.openCampaign(y, 'solare', `${y}-01-01`, `${y}-12-31`);
            C.state.campaigns = await api.getCampaigns();
        } catch(e) { /* già esiste */ }
    }

    renderCampaignsSection();
    initCampaignHandlers();
}

export function renderCampaignsSection() {
    const toggleSplit = document.getElementById('toggle-split-campaigns');
    if (toggleSplit && C.companyInfo) {
        toggleSplit.checked = C.companyInfo.split_campaigns === 1;
    }

    const campaigns = C.state.campaigns;
    const openCamp = campaigns.find(c => c.status === 'aperta');
    const activeCamp = openCamp || campaigns[0];

    // Popola selettore anno
    const sel = document.getElementById('campaign-year-selector');
    if (sel) {
        sel.innerHTML = '';
        campaigns.forEach(c => {
            const opt = document.createElement('option');
            opt.value = c.year;
            opt.textContent = `${c.year}${c.status === 'aperta' ? ' ✓' : ' (chiusa)'}`;
            if (c.year === C.state.currentCampaignYear) opt.selected = true;
            sel.appendChild(opt);
        });
    }

    // Status bar
    const statusLabel = document.getElementById('campaign-status-label');
    const statusDates = document.getElementById('campaign-status-dates');
    const closeBtn = document.getElementById('btn-close-campaign');
    if (activeCamp) {
        const isOpen = activeCamp.status === 'aperta';
        statusLabel && (statusLabel.textContent = `Annata ${activeCamp.year} — ${isOpen ? 'Aperta 🟢' : 'Chiusa 🔒'}`);
        statusDates && (statusDates.textContent = `${formatDateIT(activeCamp.start_date)} – ${formatDateIT(activeCamp.end_date)}`);
        const bar = document.getElementById('campaign-status-bar');
        if (bar) {
            bar.style.background = isOpen ? 'rgba(63,185,80,0.08)' : 'rgba(248,81,73,0.06)';
            bar.style.borderColor = isOpen ? 'rgba(63,185,80,0.25)' : 'rgba(248,81,73,0.2)';
        }
        if (closeBtn) closeBtn.style.display = isOpen ? '' : 'none';
    }

    // Tabella archivio
    const tbody = document.getElementById('campaigns-table-body');
    if (!tbody) return;
    if (campaigns.length === 0) {
        tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;color:var(--text-muted);">Nessuna annata registrata.</td></tr>';
        return;
    }
    tbody.innerHTML = '';
    campaigns.forEach(c => {
        const isOpen = c.status === 'aperta';
        const tr = document.createElement('tr');
        tr.innerHTML = `
            <td><strong>${c.year}</strong></td>
            <td><span class="badge ${c.year_type === 'agrario' ? 'badge-warning' : 'badge-info'}">${c.year_type === 'solare' ? 'Solare (Seminativi)' : c.year_type === 'agrario' ? 'Agrario (Frutteti)' : 'Personalizzata'}</span></td>
            <td style="font-size:12px;">${formatDateIT(c.start_date)} – ${formatDateIT(c.end_date)}</td>
            <td>${isOpen ? '<span class="badge badge-success">🟢 Aperta</span>' : '<span class="badge" style="background:rgba(100,100,100,0.2);color:var(--text-muted)">🔒 Chiusa</span>'}</td>
            <td style="display:flex; gap:6px; align-items:center;">
                <button class="btn btn-secondary btn-sm camp-view-btn" data-year="${c.year}" title="Visualizza registro attività">
                    <i class="fa-solid fa-eye"></i> Visualizza
                </button>
                ${isOpen ? `
                <button class="btn btn-danger btn-sm camp-close-btn" data-year="${c.year}" title="Chiudi Annata">
                    <i class="fa-solid fa-lock"></i> Chiudi Annata
                </button>` : ''}
            </td>`;

        const viewBtn = tr.querySelector('.camp-view-btn');
        viewBtn.addEventListener('click', () => {
            C.state.currentCampaignYear = c.year;
            const sel = document.getElementById('campaign-year-selector');
            if (sel) sel.value = c.year;
            loadApplicationData(c.year);
            document.querySelector('[data-tab="treatment-log"]')?.click();
        });

        const rowCloseBtn = tr.querySelector('.camp-close-btn');
        if (rowCloseBtn) {
            rowCloseBtn.addEventListener('click', async () => {
                if (!confirm(`Sei sicuro di voler chiudere l'annata ${c.year}? Nessuna nuova attività potrà essere aggiunta su quest'annata fino ad una eventuale riapertura.`)) return;
                try {
                    await api.closeCampaign(c.year);
                    alert(`Annata ${c.year} chiusa con successo.`);
                    await loadApplicationData();
                } catch(err) {
                    alert('Errore chiusura annata: ' + err.message);
                }
            });
        }

        tbody.appendChild(tr);
    });
}

export function initCampaignHandlers() {
    const toggleSplit = document.getElementById('toggle-split-campaigns');
    if (toggleSplit) {
        toggleSplit.addEventListener('change', async (e) => {
            const isSplit = e.target.checked;
            try {
                await api.updateSplitCampaigns(isSplit);
                C.companyInfo.split_campaigns = isSplit ? 1 : 0;
                alert(isSplit ? 'Gestione Quaderni Separati attivata.' : 'Gestione Quaderni Separati disattivata.');
            } catch(err) {
                alert('Errore salvataggio: ' + err.message);
                e.target.checked = !isSplit; // revert
            }
        });
    }

    // Cambio anno dal selettore
    const sel = document.getElementById('campaign-year-selector');
    if (sel) {
        sel.addEventListener('change', () => {
            if (sel.value) loadApplicationData(sel.value);
        });
    }

    // Pulsante "Nuova Annata"
    const btnNew = document.getElementById('btn-new-campaign');
    const modal = document.getElementById('modal-new-campaign');
    if (btnNew && modal) {
        btnNew.addEventListener('click', () => {
            // Pre-compila con l'anno prossimo
            const nextYear = (parseInt(C.state.currentCampaignYear) + 1).toString();
            document.getElementById('new-campaign-year').value = nextYear;
            document.getElementById('new-campaign-start').value = `${nextYear}-01-01`;
            document.getElementById('new-campaign-end').value = `${nextYear}-12-31`;
            // Popola lista rotazioni
            const rotList = document.getElementById('rotation-fields-list');
            if (rotList) {
                const openFields = C.state.fields.filter(f => !isPermanentCrop(f.crop));
                if (openFields.length === 0) {
                    rotList.innerHTML = '<p style="font-size:12px;color:var(--text-muted);">Nessun campo aperto da aggiornare.</p>';
                } else {
                    rotList.innerHTML = openFields.map(f => `
                        <div class="form-row" style="margin-bottom:8px;">
                            <div class="form-group col-5" style="margin:0;">
                                <small style="color:var(--text-muted);">${f.name}</small>
                                <div style="font-size:11px;color:var(--text-muted);">Attuale: ${f.crop}</div>
                            </div>
                            <div class="form-group col-7" style="margin:0;">
                                <input type="text" class="rotation-crop-input" data-field-id="${f.id}"
                                    placeholder="Nuova coltura ${nextYear}" value="${f.crop}" style="width:100%;font-size:13px;"/>
                            </div>
                        </div>`).join('');
                }
            }
            // Auto-calcola date in base al tipo anno
            document.getElementById('new-campaign-type').addEventListener('change', (e) => {
                const y = document.getElementById('new-campaign-year').value || nextYear;
                if (e.target.value === 'solare') {
                    document.getElementById('new-campaign-start').value = `${y}-01-01`;
                    document.getElementById('new-campaign-end').value = `${y}-12-31`;
                } else if (e.target.value === 'agrario') {
                    const prevY = (parseInt(y) - 1).toString();
                    document.getElementById('new-campaign-start').value = `${prevY}-10-01`;
                    document.getElementById('new-campaign-end').value = `${y}-09-30`;
                }
            }, { once: true });
            modal.style.display = 'flex';
        });
    }

    // Annulla
    document.getElementById('btn-cancel-campaign')?.addEventListener('click', () => {
        if (modal) modal.style.display = 'none';
    });

    // Conferma apertura nuova annata
    document.getElementById('btn-confirm-campaign')?.addEventListener('click', async () => {
        const year = document.getElementById('new-campaign-year').value.trim();
        const yearType = document.getElementById('new-campaign-type').value;
        const startDate = document.getElementById('new-campaign-start').value;
        const endDate = document.getElementById('new-campaign-end').value;
        if (!year || !startDate || !endDate) { alert('Compila tutti i campi.'); return; }
        try {
            await api.openCampaign(year, yearType, startDate, endDate);
            // Salva le rotazioni colture per i campi aperti
            const inputs = document.querySelectorAll('.rotation-crop-input');
            for (const inp of inputs) {
                const fieldId = inp.dataset.fieldId;
                const crop = inp.value.trim();
                if (fieldId && crop) await api.saveFieldRotation(fieldId, year, crop);
            }
            if (modal) modal.style.display = 'none';
            alert(`Annata ${year} aperta con successo!`);
            await loadApplicationData(year);
        } catch(err) {
            alert('Errore: ' + err.message);
        }
    });

    // Chiudi annata corrente
    document.getElementById('btn-close-campaign')?.addEventListener('click', async () => {
        const camp = C.state.campaigns.find(c => c.status === 'aperta');
        if (!camp) return;
        if (!confirm(`Sei sicuro di voler chiudere l'annata ${camp.year}? Non sarà più possibile aggiungere attività su quest'annata.`)) return;
        try {
            await api.closeCampaign(camp.year);
            alert(`Annata ${camp.year} chiusa e archiviata.`);
            await loadApplicationData();
        } catch(err) {
            alert('Errore: ' + err.message);
        }
    });
}

// ----- SETTINGS & OPERATOR MANAGEMENT -----

export function initSettingsTab() {
    const companyForm = document.getElementById('settings-company-form');
    if (companyForm) {
        companyForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            if (C.currentUser && C.currentUser.isDemo) {
                showRegisterRequiredAlert('salvare le impostazioni aziendali');
                return;
            }
            const name = document.getElementById('settings-company-name').value.trim();
            const cuaa = document.getElementById('settings-company-cuaa').value.trim();
            
            try {
                await api.setupCompany(name, cuaa);
                alert('Impostazioni azienda salvate correttamente!');
                await refreshSettingsView();
                await loadApplicationData();
            } catch (err) {
                alert('Errore nel salvataggio delle impostazioni: ' + err.message);
            }
        });
    }
    
    // SIAN Guide Modal
    const btnOpenSianGuide = document.getElementById('btn-open-sian-guide');
    const modalSianGuide = document.getElementById('sian-guide-modal');
    if (btnOpenSianGuide && modalSianGuide) {
        btnOpenSianGuide.addEventListener('click', () => {
            modalSianGuide.style.display = 'flex';
            setTimeout(() => modalSianGuide.classList.add('show'), 10);
        });

        const closeSianModal = () => {
            modalSianGuide.classList.remove('show');
            setTimeout(() => modalSianGuide.style.display = 'none', 300);
        };

        const closeSianGuideBtn = document.getElementById('close-sian-guide-modal');
        if (closeSianGuideBtn) closeSianGuideBtn.addEventListener('click', closeSianModal);
        modalSianGuide.addEventListener('click', (e) => {
            if (e.target === modalSianGuide) closeSianModal();
        });
    }
    
    const btnSianHelpSettings = document.getElementById('btn-sian-help-settings');
    if (btnSianHelpSettings && modalSianGuide) {
        btnSianHelpSettings.addEventListener('click', () => {
            modalSianGuide.style.display = 'flex';
            setTimeout(() => modalSianGuide.classList.add('show'), 10);
        });
    }
    
    // Operator Form CRUD
    const operatorForm = document.getElementById('operator-manage-form');
    if (operatorForm) {
        const btnAddToggle = document.getElementById('btn-add-operator-toggle');
        if (btnAddToggle) {
            btnAddToggle.addEventListener('click', () => {
                const container = document.getElementById('operator-form-container');
                container.classList.toggle('hidden');
                if (!container.classList.contains('hidden')) {
                    operatorForm.reset();
                    C.currentEditingOperatorId = null;
                    document.getElementById('btn-save-operator').innerHTML = '<i class="fa-solid fa-user-plus"></i> Salva Operatore';
                    document.getElementById('operator-form-title').textContent = 'Aggiungi Nuovo Operatore';
                    document.getElementById('operator-email').disabled = false;
                    document.getElementById('operator-role').disabled = false;
                    document.getElementById('operator-name').focus();
                }
            });
        }

        operatorForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            if (C.currentUser && C.currentUser.isDemo) {
                showRegisterRequiredAlert('gestire gli operatori aziendali');
                return;
            }
            const email = document.getElementById('operator-email').value.trim();
            const name = document.getElementById('operator-name').value.trim();
            const role = document.getElementById('operator-role').value;
            const patent = document.getElementById('operator-patent').value.trim() || null;
            const isDelegate = document.getElementById('operator-is-delegate')?.checked || false;
            
            try {
                if (C.currentEditingOperatorId) {
                    await api.updateOperator(C.currentEditingOperatorId, { name, role, patent, isDelegate });
                    alert('Operatore aggiornato con successo!');
                } else {
                    await api.createOperator({ email, name, role, patent, isDelegate });
                    alert('Operatore invitato con successo! Riceverà un\'email OTP al primo accesso.');
                }
                operatorForm.reset();
                C.currentEditingOperatorId = null;
                document.getElementById('btn-save-operator').innerHTML = '<i class="fa-solid fa-user-plus"></i> Salva Operatore';
                document.getElementById('operator-email').disabled = false;
                document.getElementById('operator-role').disabled = false;
                const delChk = document.getElementById('operator-is-delegate');
                if (delChk) delChk.checked = false;
                await renderOperatorsList();
            } catch (err) {
                alert('Errore nel salvataggio dell\'operatore: ' + err.message);
            }
        });
    }
    
    // Cancel edit button if present or reset form
    const btnCancelEdit = document.getElementById('btn-cancel-operator');
    if (btnCancelEdit) {
        btnCancelEdit.addEventListener('click', () => {
            operatorForm.reset();
            C.currentEditingOperatorId = null;
            document.getElementById('btn-save-operator').innerHTML = '<i class="fa-solid fa-user-plus"></i> Salva Operatore';
            document.getElementById('operator-email').disabled = false;
            document.getElementById('operator-role').disabled = false;
            const delChk = document.getElementById('operator-is-delegate');
            if (delChk) delChk.checked = false;
            document.getElementById('operator-form-container').classList.add('hidden');
        });
    }

    // SIAN Mode Radio Toggles
    const sianRadios = document.querySelectorAll('input[name="sian-mode"]');
    sianRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
            updateSianModeVisibility(e.target.value);
        });
    });

    // Certificato SIAN file input listener
    const certInput = document.getElementById('settings-sian-cert');
    if (certInput) {
        certInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (!file) {
                C.settingsCertContent = null;
                C.settingsCertName = null;
                return;
            }
            C.settingsCertName = file.name;
            const reader = new FileReader();
            reader.onload = function(evt) {
                C.settingsCertContent = evt.target.result;
            };
            reader.readAsText(file);
        });
    }

    // Save SIAN Delegation button
    const btnSaveSianDelegation = document.getElementById('btn-save-sian-delegation');
    if (btnSaveSianDelegation) {
        btnSaveSianDelegation.addEventListener('click', async () => {
            if (C.currentUser && C.currentUser.isDemo) {
                showRegisterRequiredAlert('salvare la delega SIAN');
                return;
            }
            const selectedMode = document.querySelector('input[name="sian-mode"]:checked')?.value || 'caa_partner';
            const caaPartner = document.getElementById('sian-caa-partner')?.value.trim() || null;
            const signed = document.getElementById('sian-delegation-signed')?.checked || false;

            if (selectedMode === 'caa_partner' && !caaPartner) {
                alert('Seleziona un CAA Convenzionato dal menu a tendina per attivare la delega.');
                return;
            }

            if (selectedMode === 'direct_webservice') {
                const apiKey = document.getElementById('settings-sian-api-key')?.value.trim() || '';
                if (!apiKey) {
                    alert('Inserisci l\'API Key SIAN.');
                    return;
                }
                if (!C.settingsCertContent && (!C.companyInfo || !C.companyInfo.sian_cert_name)) {
                    alert('Carica un file di certificato valido.');
                    return;
                }
            }

            if (!signed) {
                alert('Spunta la casella di autorizzazione delega per procedere.');
                return;
            }

            btnSaveSianDelegation.disabled = true;
            btnSaveSianDelegation.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Salvataggio...';

            try {
                if (selectedMode === 'direct_webservice') {
                    const apiKey = document.getElementById('settings-sian-api-key')?.value.trim() || '';
                    const resSian = await api.updateSian(apiKey, C.companyInfo.cuaa, C.settingsCertContent || null, C.settingsCertName || null);
                    C.companyInfo = resSian.company;
                    // Reset dati file transitori
                    C.settingsCertContent = null;
                    C.settingsCertName = null;
                    const certFileEl = document.getElementById('settings-sian-cert');
                    if (certFileEl) certFileEl.value = '';
                }

                const res = await api.updateSianDelegation(
                    selectedMode === 'caa_partner' ? caaPartner : null, 
                    selectedMode, 
                    signed
                );
                C.companyInfo = res.company;
                alert(`Delega SIAN registrata con successo!`);
                updateSianStatusBadge();
                populateSettingsCompanyFields();
            } catch (err) {
                alert('Errore salvataggio delega SIAN: ' + err.message);
            } finally {
                btnSaveSianDelegation.disabled = false;
                btnSaveSianDelegation.innerHTML = '<i class="fa-solid fa-cloud-arrow-up"></i> Salva Delega SIAN';
            }
        });
    }

    // Export QDCA XML button
    const btnExportXml = document.getElementById('btn-export-qdca-xml');
    if (btnExportXml) {
        btnExportXml.addEventListener('click', () => {
            generateQdcaXml();
        });
    }
}

export function updateSianModeVisibility(mode) {
    const caaContainer = document.getElementById('sian-caa-container');
    const directContainer = document.getElementById('sian-direct-container');
    if (caaContainer) caaContainer.classList.add('hidden');
    if (directContainer) directContainer.classList.add('hidden');
}

export function updateSianStatusBadge() {
    const badge = document.getElementById('sian-status-badge');
    if (!badge) return;

    badge.className = 'badge badge-warning';
    badge.style.background = '#d97706';
    badge.innerHTML = '📥 Export manuale (sync automatica in stand-by)';
}

export async function refreshSettingsView() {
    if (C.currentUser?.isDemo) {
        populateSettingsCompanyFields();
        const tbody = document.getElementById('operators-table-body');
        if (tbody) {
            tbody.innerHTML = '<tr><td colspan="5" style="text-align: center; color: var(--text-muted);"><i class="fa-solid fa-lock" style="margin-right:8px;"></i>Registrati per gestire gli operatori aziendali.</td></tr>';
        }
        return;
    }
    if (!C.currentUser) return;

    try {
        const me = await api.getMe();
        if (me.user) C.currentUser = me.user;
        if (me.company) C.companyInfo = me.company;
    } catch (err) {
        console.warn('[AgriLog] refreshSettings getMe:', err.message);
    }

    if (!C.companyInfo && C.currentUser.company_id) {
        try {
            const res = await api.getCompany();
            C.companyInfo = res.company;
        } catch (err) {
            console.warn('[AgriLog] refreshSettings getCompany:', err.message);
        }
    }

    populateSettingsCompanyFields();
    await renderOperatorsList();
}

export function populateSettingsCompanyFields() {
    if (!C.companyInfo) {
        const nameEl = document.getElementById('settings-company-name');
        const cuaaEl = document.getElementById('settings-company-cuaa');
        if (nameEl) nameEl.value = '';
        if (cuaaEl) cuaaEl.value = '';
        return;
    }
    document.getElementById('settings-company-name').value = C.companyInfo.name || '';
    document.getElementById('settings-company-cuaa').value = C.companyInfo.cuaa || '';

    const radios = document.querySelectorAll('input[name="sian-mode"]');
    radios.forEach(r => {
        r.checked = (r.value === 'manual_export');
    });

    updateSianModeVisibility('manual_export');
    updateSianStatusBadge();
}

export function generateQdcaXml() {
    if (C.currentUser && C.currentUser.isDemo) {
        showRegisterRequiredAlert('esportare il registro QDCA');
        return;
    }
    if (!canTransmitSian()) {
        alert('L\'export QDCA è riservato ad amministratori e professionisti abilitati (agronomo, agrotecnico, perito agrario).');
        return;
    }
    if (!C.companyInfo) {
        alert('Dati azienda non trovati.');
        return;
    }
    const treatments = C.state.treatments || [];
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<QuadernoCampagna versione="2.0" timestamp="${new Date().toISOString()}">\n`;
    xml += `  <Azienda>\n`;
    xml += `    <RagioneSociale>${C.companyInfo.name || ''}</RagioneSociale>\n`;
    xml += `    <CUAA>${C.companyInfo.cuaa || ''}</CUAA>\n`;
    xml += `    <CAAPartner>${C.companyInfo.caa_partner || 'N.D.'}</CAAPartner>\n`;
    xml += `    <StatoDelega>${C.companyInfo.sian_delegation_signed === 1 ? 'ATTIVA' : 'NON_ATTIVA'}</StatoDelega>\n`;
    xml += `  </Azienda>\n`;
    xml += `  <Trattamenti conteggio="${treatments.length}">\n`;
    
    treatments.forEach(t => {
        xml += `    <Trattamento id="${t.id}">\n`;
        xml += `      <Data>${t.date}</Data>\n`;
        xml += `      <CampoID>${t.fieldId}</CampoID>\n`;
        xml += `      <NomeCampo>${t.fieldName || ''}</NomeCampo>\n`;
        xml += `      <Coltura>${t.fieldCrop || ''}</Coltura>\n`;
        xml += `      <TipoAttivita>${t.activityType || 'trattamento'}</TipoAttivita>\n`;
        xml += `      <Prodotto>${t.product || ''}</Prodotto>\n`;
        xml += `      <Categoria>${t.category || ''}</Categoria>\n`;
        xml += `      <Quantita unit="${t.unit || 'kg/L'}">${t.quantity || 0}</Quantita>\n`;
        xml += `      <AcquaLitri>${t.water || 0}</AcquaLitri>\n`;
        xml += `      <Avversita>${t.adversity || ''}</Avversita>\n`;
        xml += `      <TempoCarenzaGiorni>${t.safetyDays || 0}</TempoCarenzaGiorni>\n`;
        xml += `      <OperatorePatentino>${C.currentUser ? (C.currentUser.patent || 'N.D.') : 'N.D.'}</OperatorePatentino>\n`;
        xml += `      <StatoSIAN>${t.sianSyncId ? 'TRASMESSO' : 'DA_TRASMETTERE'}</StatoSIAN>\n`;
        xml += `    </Trattamento>\n`;
    });
    
    xml += `  </Trattamenti>\n`;
    xml += `</QuadernoCampagna>`;

    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `QDCA_AGEA_${C.companyInfo.cuaa || 'EXPORT'}_${new Date().toISOString().slice(0,10)}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export async function renderOperatorsList() {
    // In demo mode, mostra messaggio placeholder senza chiamare l'API
    if (C.currentUser && C.currentUser.isDemo) {
        renderDemoOperatorsList();
        return;
    }
    
    const tbody = document.getElementById('operators-table-body');
    if (!tbody) return;
    
    let operators = [];
    try {
        if (C.currentUser?.company_id) {
            operators = await api.getOperators();
        }
    } catch (err) {
        console.error('Errore nel caricamento degli operatori:', err);
    }

    // Mostra sempre l'admin/utente registrato anche se l'API non lo restituisce
    if (C.currentUser?.company_id) {
        const meId = String(C.currentUser.id);
        if (!operators.some(o => String(o.id) === meId)) {
            operators.unshift({
                id: C.currentUser.id,
                name: C.currentUser.name || C.currentUser.email?.split('@')[0] || 'Amministratore',
                email: C.currentUser.email || '',
                role: C.currentUser.role || 'admin',
                patent: C.currentUser.patent || null,
                is_active: 1,
                is_delegate: C.currentUser.is_delegate || 0
            });
        }
    }

    tbody.innerHTML = '';
    
    if (operators.length === 0) {
        tbody.innerHTML = '<tr><td colspan="5" style="text-align: center; color: var(--text-muted);">Accedi con un account aziendale per gestire gli operatori.</td></tr>';
        return;
    }
    
    operators.forEach(op => {
            const row = document.createElement('tr');
            const patentText = op.patent || 'N.D.';
            const statusBadge = op.is_active ? '<span class="badge badge-success">Attivo</span>' : '<span class="badge badge-danger">Inattivo</span>';
            const roleLabels = {
                'admin': 'Amministratore',
                'agrotecnico': 'Agrotecnico Laureato',
                'agronomo': 'Dott. Agronomo',
                'perito_agrario': 'Perito Agrario',
                'fitosanitario': 'Fitosanitario',
                'trattorista': 'Trattorista',
                'altro': 'Altro'
            };
            const roleText = roleLabels[op.role] || (op.role.charAt(0).toUpperCase() + op.role.slice(1));
            const delegateBadge = op.is_delegate === 1 ? ' <span class="badge badge-warning" style="font-size:10px; background:#d97706; color:#fff;" title="Delega Amministrativa Gestione Campi & Magazzino">🔑 Delegato</span>' : '';
            row.innerHTML = `
                <td><strong>${op.name}</strong>${delegateBadge}<br><small style="color: var(--text-muted);">${op.email}</small></td>
                <td><span class="badge badge-info">${roleText}</span></td>
                <td><code>${patentText}</code></td>
                <td>${statusBadge}</td>
                <td>
                    <div style="display: flex; gap: 8px; justify-content: center;">
                        <button class="btn-icon edit-op-btn" data-id="${op.id}" title="Modifica" style="color: var(--primary);">
                            <i class="fa-solid fa-pencil"></i>
                        </button>
                        ${op.role !== 'admin' ? `
                        <button class="btn-icon delete-op-btn" data-id="${op.id}" title="Elimina Operatore" style="color: var(--danger);">
                            <i class="fa-solid fa-trash-can"></i>
                        </button>` : ''}
                    </div>
                </td>
            `;

            // Pulsante modifica — sbloccato per tutti gli operatori incluso l'Admin
            const editBtn = row.querySelector('.edit-op-btn');
            if (editBtn) {
                editBtn.addEventListener('click', () => {
                    const isAdmin = op.role === 'admin';
                    C.currentEditingOperatorId = op.id;
                    document.getElementById('operator-email').value = op.email;
                    document.getElementById('operator-email').disabled = true;
                    document.getElementById('operator-name').value = op.name;
                    const roleSelect = document.getElementById('operator-role');
                    roleSelect.value = op.role || 'fitosanitario';
                    roleSelect.disabled = false;
                    const delChk = document.getElementById('operator-is-delegate');
                    if (delChk) delChk.checked = op.is_delegate === 1;
                    document.getElementById('operator-form-title').textContent = isAdmin ? 'Modifica Profilo & Brevetto Admin' : 'Modifica Operatore';
                    document.getElementById('operator-patent').value = op.patent || '';
                    document.getElementById('operator-form-container').classList.remove('hidden');
                    document.getElementById('btn-save-operator').innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Aggiorna Operatore';
                });
            }

            // Pulsante elimina — solo per operatori non-admin
            const deleteBtn = row.querySelector('.delete-op-btn');
            if (deleteBtn) {
                deleteBtn.addEventListener('click', async () => {
                    if (C.currentUser && C.currentUser.isDemo) {
                        showRegisterRequiredAlert('eliminare operatori');
                        return;
                    }
                    if (confirm(`Sei sicuro di voler eliminare l'operatore "${op.name}"?`)) {
                        try {
                            await api.deleteOperator(op.id);
                            await renderOperatorsList();
                        } catch (err) {
                            alert('Errore nell\'eliminazione dell\'operatore: ' + err.message);
                        }
                    }
                });
            }

            tbody.appendChild(row);
        });
}

// ─── SYSTEM: CODA OFFLINE & AUTO-SYNC PWA ──────────────────────────────────────

export function getOfflineQueue() {
    try {
        return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
    } catch {
        return [];
    }
}

export function saveOfflineQueue(queue) {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
    updateOfflineQueueUI();
}

export function addActivityToOfflineQueue(activityData) {
    const queue = getOfflineQueue();
    queue.push({
        ...activityData,
        _offlineId: 'off_' + Date.now(),
        _savedAt: new Date().toISOString()
    });
    saveOfflineQueue(queue);
    
    // Inserisci temporaneamente nello stato per visibilità immediata nell'interfaccia
    C.state.treatments.unshift({
        ...activityData,
        id: 'off_' + Date.now(),
        sian_sync_id: null,
        _isOfflinePending: true
    });
    renderRecentTreatments();
    populateLogCropFilter();
    renderFullLogTable();
    renderCarenzeCalendar();
}

export function updateOfflineQueueUI() {
    const queue = getOfflineQueue();
    const badge = document.getElementById('offline-queue-badge');
    const countEl = document.getElementById('offline-queue-count');
    if (!badge || !countEl) return;

    if (queue.length > 0) {
        countEl.textContent = queue.length;
        badge.classList.remove('hidden');
    } else {
        badge.classList.add('hidden');
    }
}

export async function syncOfflineQueue() {
    const queue = getOfflineQueue();
    if (queue.length === 0) return;
    if (!navigator.onLine) return;

    console.log(`📡 [OFFLINE SYNC] Tentativo di sincronizzazione di ${queue.length} attività in coda...`);
    const badge = document.getElementById('offline-queue-badge');
    if (badge) {
        badge.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sincronizzazione...';
    }

    const remainingQueue = [];
    let syncedCount = 0;

    for (const item of queue) {
        try {
            const cleanData = { ...item };
            delete cleanData._offlineId;
            delete cleanData._savedAt;
            delete cleanData._isOfflinePending;
            await api.saveActivity(cleanData);
            if (cleanData.activityType === 'trattamento' && cleanData.product && cleanData.quantity > 0) {
                const inventory = await api.getInventory();
                const warehouseProduct = inventory.find(p => p.name.toLowerCase() === cleanData.product.toLowerCase());
                if (warehouseProduct) {
                    const newStock = parseFloat((warehouseProduct.stock - cleanData.quantity).toFixed(2));
                    await api.updateStock(warehouseProduct.id, newStock).catch(() => {});
                }
            }
            syncedCount++;
        } catch (err) {
            console.error(`[OFFLINE SYNC] Errore sincronizzazione attività:`, err);
            remainingQueue.push(item);
        }
    }

    saveOfflineQueue(remainingQueue);

    if (syncedCount > 0) {
        alert(`🟢 Sincronizzazione completata! ${syncedCount} attività salvate offline sono state trasmesse con successo al server.`);
        await loadApplicationData();
    }
}

// Inizializza listener per coda offline
window.addEventListener('online', () => {
    console.log('📡 Rete ripristinata: avvio sincronizzazione automatica...');
    syncOfflineQueue();
});
setInterval(() => {
    if (navigator.onLine && getOfflineQueue().length > 0) {
        syncOfflineQueue();
    }
}, 30000);

document.addEventListener('DOMContentLoaded', () => {
    const badge = document.getElementById('offline-queue-badge');
    if (badge) {
        badge.addEventListener('click', () => syncOfflineQueue());
    }
    updateOfflineQueueUI();
});

// ─── SYSTEM: CALENDARIO CARENZE & SICUREZZA CAMPO ─────────────────────────────

export function setCarenzeFilterMode(mode) {
    C.carenzeFilterMode = mode;
    const btnAll = document.getElementById('btn-filter-carenze-all');
    const btnActive = document.getElementById('btn-filter-carenze-active');
    if (btnAll && btnActive) {
        if (mode === 'all') {
            btnAll.classList.add('active');
            btnActive.classList.remove('active');
        } else {
            btnActive.classList.add('active');
            btnAll.classList.remove('active');
        }
    }
    renderCarenzeCalendar();
};

export function getTreatmentCarenzeList() {
    const carenzeIndex = new Map();
    if (Array.isArray(C.state.carenzeTreatments)) {
        C.state.carenzeTreatments.forEach(t => {
            if (t && (t.id || t.date)) {
                carenzeIndex.set(t.id || `${t.fieldId || t.field_id}_${t.date}_${t.product}`, t);
            }
        });
    }
    if (Array.isArray(C.state.treatments)) {
        C.state.treatments.forEach(t => {
            if (t && (t.id || t.date)) {
                carenzeIndex.set(t.id || `${t.fieldId || t.field_id}_${t.date}_${t.product}`, t);
            }
        });
    }
    return Array.from(carenzeIndex.values());
}

export function renderCarenzeCalendar() {
    try {
        const activeStatEl = document.getElementById('carenze-stat-active');
        const fieldsStatEl = document.getElementById('carenze-stat-fields');
        const nextStatEl = document.getElementById('carenze-stat-next');
        const tbody = document.getElementById('carenze-table-body');

        console.log('[CARENZE v84] tbody:', tbody, '| treatments count:', (C.state.treatments||[]).length, '| carenzeTreatments count:', (C.state.carenzeTreatments||[]).length);

        if (!tbody) {
            console.error('[CARENZE v84] TBODY NULL - carenze-table-body non trovato nel DOM!');
            return;
        }

        const treatments = getTreatmentCarenzeList();
        console.log('[CARENZE v84] getTreatmentCarenzeList() count:', treatments.length);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const carenzeItems = [];
        const blockedFields = new Set();
        let nextClearingDate = null;
        let activeCount = 0;

        treatments.forEach(t => {
            if (!t || !t.date) return;

            const actType = (t.activityType || t.activity_type || '').toLowerCase();
            const category = (t.category || '').toLowerCase();
            const product = t.product || t.prodotto || t.description || '';
            const sDays = parseInt(t.safetyDays ?? t.safety_days ?? t.tempoCarenzaGiorni ?? t.tempo_carenza_giorni) || 0;

            const isNonTreatment = actType === 'lavorazione' || actType === 'concimazione' || actType === 'semina' || actType === 'irrigazione' || actType === 'raccolta';
            if (isNonTreatment && sDays <= 0) return;

            const treatDate = parseDateRobust(t.date);
            if (!treatDate) return;
            treatDate.setHours(0, 0, 0, 0);

            const fid = t.fieldId || t.field_id;
            const field = (C.state.fields && Array.isArray(C.state.fields)) ? C.state.fields.find(f => String(f.id) === String(fid)) : null;
            const fieldName = t.fieldName || t.field_name || (field ? field.name : 'Campo N.D.');
            const fieldCrop = t.fieldCrop || t.field_crop || (field ? field.crop : 'Coltura N.D.');

            const expiryDate = new Date(treatDate);
            expiryDate.setDate(expiryDate.getDate() + sDays);

            const isActive = sDays > 0 && expiryDate >= today;

            if (isActive) {
                activeCount++;
                if (fid) blockedFields.add(String(fid));
                if (!nextClearingDate || expiryDate < nextClearingDate) {
                    nextClearingDate = expiryDate;
                }
            }

            carenzeItems.push({
                ...t,
                fieldName,
                fieldCrop,
                product: product || 'Fitosanitario',
                treatDate,
                expiryDate,
                safetyDays: sDays,
                isActive
            });
        });

        if (activeStatEl) activeStatEl.textContent = activeCount;
        if (fieldsStatEl) fieldsStatEl.textContent = blockedFields.size;
        if (nextStatEl) {
            nextStatEl.textContent = nextClearingDate 
                ? nextClearingDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
                : '-';
        }

        const displayItems = C.carenzeFilterMode === 'active' 
            ? carenzeItems.filter(item => item.isActive)
            : carenzeItems;

        console.log('[CARENZE DEBUG] C.carenzeFilterMode:', C.carenzeFilterMode, '| carenzeItems.length:', carenzeItems.length, '| displayItems.length:', displayItems.length);
        if (carenzeItems.length > 0) {
            carenzeItems.forEach((c, i) => {
                console.log(`[CARENZE DEBUG] item[${i}]:`, c.fieldName, '| sDays:', c.safetyDays, '| isActive:', c.isActive, '| treatDate:', c.treatDate, '| expiryDate:', c.expiryDate);
            });
        }

        tbody.innerHTML = '';
        console.log('[CARENZE DEBUG] tbody exists?', !!tbody, '| tbody.id:', tbody?.id);

        if (displayItems.length === 0) {
            const msg = C.carenzeFilterMode === 'active'
                ? 'Nessuna carenza o divieto di rientro ATTIVO al momento nei tuoi terreni.'
                : 'Nessun trattamento fitosanitario registrato nel quaderno di campagna.';
            tbody.innerHTML = `<tr><td colspan="7" style="text-align: center; color: var(--text-muted); padding: 24px;"><i class="fa-solid fa-check-circle text-success" style="margin-right: 8px; font-size: 16px;"></i>${msg}</td></tr>`;
            return;
        }

        displayItems.sort((a, b) => {
            if (a.isActive && !b.isActive) return -1;
            if (!a.isActive && b.isActive) return 1;
            if (a.isActive && b.isActive) return a.expiryDate - b.expiryDate;
            return b.treatDate - a.treatDate;
        });

        displayItems.forEach(c => {
            const row = document.createElement('tr');
            const treatDateStr = c.treatDate.toLocaleDateString('it-IT');
            const expiryDateStr = c.safetyDays > 0 ? c.expiryDate.toLocaleDateString('it-IT') : 'Nessuna';
            
            const diffMs = c.expiryDate - today;
            const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

            let statusBadge = '';
            if (c.safetyDays <= 0) {
                statusBadge = `<span class="badge badge-secondary" style="background: rgba(108, 117, 125, 0.15); color: #a0aec0; border: 1px solid #4a5568;">
                    <i class="fa-solid fa-circle-info"></i> Nessuna carenza (0 gg)
                </span>`;
            } else if (c.isActive) {
                statusBadge = `<span class="badge badge-warning" style="background: rgba(219, 109, 40, 0.15); color: var(--warning-hover); border: 1px solid var(--warning);">
                    <i class="fa-solid fa-ban"></i> In Carenza (-${diffDays} gg)
                </span>`;
            } else {
                statusBadge = `<span class="badge badge-success" style="background: rgba(45, 164, 78, 0.15); color: #2ea44f; border: 1px solid #2ea44f;">
                    <i class="fa-solid fa-circle-check"></i> Svincolato / Campo Libero
                </span>`;
            }

            row.style.backgroundColor = '#ffffff';
            row.style.color = '#0f172a';
            row.innerHTML = `
                <td style="color: #0f172a !important; font-weight: 600;">${c.fieldName}</td>
                <td><span class="badge badge-info" style="background: rgba(2, 132, 199, 0.12); color: #0284c7; border: 1px solid rgba(2, 132, 199, 0.3); font-weight: 600;">${c.fieldCrop}</span></td>
                <td style="color: #0f172a !important; font-weight: 600;">${c.product}</td>
                <td style="color: #334155 !important;">${treatDateStr}</td>
                <td><code style="background: #f1f5f9; color: #0f172a; padding: 3px 8px; border-radius: 6px; border: 1px solid #cbd5e1; font-weight: 600;">${c.safetyDays} giorni</code></td>
                <td><strong style="color: ${c.isActive ? '#d97706' : '#64748b'}; font-weight: 700;">${expiryDateStr}</strong></td>
                <td>${statusBadge}</td>
            `;
            tbody.appendChild(row);
        });

        // DEBUG POST-RENDER: controlla DOM 500ms dopo
        const section = document.getElementById('tab-carenze-calendar');
        console.log('[CARENZE v85 POST-RENDER] tbody.rows.length SUBITO:', tbody.rows.length);
        console.log('[CARENZE v85 POST-RENDER] section display SUBITO:', section ? window.getComputedStyle(section).display : 'NULL');
        console.log('[CARENZE v85 POST-RENDER] section offsetHeight:', section ? section.offsetHeight : 'NULL');
        setTimeout(() => {
            console.log('[CARENZE v85 AFTER 500ms] tbody.rows.length:', tbody.rows.length);
            console.log('[CARENZE v85 AFTER 500ms] section display:', section ? window.getComputedStyle(section).display : 'NULL');
            console.log('[CARENZE v85 AFTER 500ms] section offsetHeight:', section ? section.offsetHeight : 'NULL');
            console.log('[CARENZE v85 AFTER 500ms] section innerHTML snippet:', section ? section.innerHTML.substring(0, 200) : 'NULL');
        }, 500);

    } catch (err) {
        console.error('[AgriLog] ERRORE IN CALENDARIO CARENZE:', err);
    }
}

export function checkFieldSafetyRestriction(fieldId) {
    const treatments = getTreatmentCarenzeList();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (const t of treatments) {
        const sDays = parseInt(t.safetyDays ?? t.safety_days ?? t.tempoCarenzaGiorni ?? t.tempo_carenza_giorni) || 0;
        const fId = t.fieldId || t.field_id;
        if (String(fId) === String(fieldId) && sDays > 0 && t.date) {
            const treatDate = parseDateRobust(t.date);
            if (!treatDate) continue;
            treatDate.setHours(0, 0, 0, 0);
            const expiryDate = new Date(treatDate);
            expiryDate.setDate(expiryDate.getDate() + sDays);

            if (expiryDate >= today) {
                return {
                    isInRestriction: true,
                    product: t.product || t.prodotto || 'Fitosanitario',
                    expiryDate,
                    safetyDays: sDays
                };
            }
        }
    }
    return { isInRestriction: false };
}
